/**
 * @file SicCodeDropdown.js
 * @description A dropdown component for selecting a SIC code with optional search functionality.
 */

import React, { useState, useEffect } from "react";
import { fetchSicCodes } from "../../../services/systemService";
import { Dropdown } from "../../helpers/DropdownSearchableSingleSelect";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * A dropdown component for selecting SIC codes.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label should be displayed inline.
 * @param {string} [props.selectedSicCode] - The currently selected SIC code.
 * @param {Function} props.onSicCodeChange - The callback function to be called when the SIC code is changed.
 * @param {string} [props.label="SIC Code"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowSearch=false] - Enables search functionality in the dropdown.
 * @returns {JSX.Element} The rendered SicCodeDropdown component.
 */
const SicCodeDropdown = ({
  className,
  inlineLabel = true,
  selectedSicCode,
  onSicCodeChange,
  label = "SIC Code",
  isRequired = false,
  allowSearch = false,
}) => {
  const [sicCodes, setSicCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getSicCodes = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSicCodes();
        setSicCodes(data);
      } catch (error) {
        console.error("Could not fetch SIC codes:", error);
        Toast.error(getText('msg_4a785a6cafb5'));
      } finally {
        setIsLoading(false);
      }
    };

    getSicCodes();
  }, []);

  const getDefaultLabel = () => {
    const matchingSicCode = sicCodes.find(
      (sicCode) => sicCode.id === selectedSicCode
    );
    return matchingSicCode ? matchingSicCode.name : "Select SIC Code";
  };

  const sicCodeOptions = sicCodes.map((sicCode) => ({
    value: sicCode.id,
    label: `${sicCode.name}`,
  }));

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="sic-code-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="sic-code-dropdown"
          defaultValue={getDefaultLabel()}
          options={sicCodeOptions}
          value={selectedSicCode}
          onChange={onSicCodeChange}
          disabled={sicCodes.length === 0 || isLoading}
          allowSearch={allowSearch}
        />
      </LoadingWrapper>
    </div>
  );
};

export default SicCodeDropdown;
